import { Button, confirm, Modal } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Edit,
  ExternalLink,
  FileText,
  Plus,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Background,
  Controls,
  type Edge,
  MiniMap,
  type Node,
  type NodeMouseHandler,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "reactflow";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import "reactflow/dist/style.css";
import {
  useCreateFlowMapping,
  useDeleteFlowMapping,
  useFlowMappings,
  useFlowQuery,
  useUpdateFlow,
  useUpdateFlowMapping,
} from "../api/queries";
import { FlowForm } from "../ui/FlowForm";
import { FlowMappingForm } from "../ui/FlowMappingForm";
import { FlowStepNode } from "../ui/FlowStepNode";
import { statusLabels } from "../utils/labels";

// Define node types for React Flow
const nodeTypes = {
  flowStep: FlowStepNode,
};

// Helper function to determine best handle positions based on node positions
function getHandlePositions(
  sourceNode: Node | undefined,
  targetNode: Node | undefined,
): { sourceHandle: string; targetHandle: string } {
  if (!sourceNode || !targetNode) {
    return { sourceHandle: "right", targetHandle: "left" };
  }

  const sourceX = sourceNode.position.x;
  const sourceY = sourceNode.position.y;
  const targetX = targetNode.position.x;
  const targetY = targetNode.position.y;

  // Calculate differences
  const deltaX = Math.abs(targetX - sourceX);
  const deltaY = Math.abs(targetY - sourceY);

  // If vertical alignment is stronger (more vertical than horizontal)
  if (deltaY > deltaX) {
    if (targetY > sourceY) {
      // Target is below source
      return { sourceHandle: "bottom", targetHandle: "top" };
    }
    // Target is above source
    return { sourceHandle: "top", targetHandle: "bottom" };
  }
  // Horizontal alignment
  if (targetX > sourceX) {
    // Target is to the right of source
    return { sourceHandle: "right", targetHandle: "left" };
  }
  // Target is to the left of source
  return { sourceHandle: "left", targetHandle: "right" };
}

export function FlowDetailPage() {
  const { id } = useParams({ from: "/app/flows/$id" });
  const nav = useNavigate();
  const flow = useFlowQuery(id, true, true);
  const mappings: ReturnType<typeof useFlowMappings> = useFlowMappings(id);
  const canUpdate = useActionPermission(["flows:update"]);
  const editFlowModal = useDisclosure();
  const updateFlow = useUpdateFlow(id);
  const createMappingModal = useDisclosure();
  const editMappingModal = useDisclosure();
  const [editingMapping, setEditingMapping] = useState<{
    id: string;
    sopId: string | null;
    positionId: string | null;
    departmentId: string | null;
  } | null>(null);
  const createMapping = useCreateFlowMapping(id);
  const updateMapping = useUpdateFlowMapping(id);
  const deleteMapping = useDeleteFlowMapping();
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  // Convert flow steps to ReactFlow nodes
  const initialNodes = useMemo<Node[]>(() => {
    if (!flow.data?.steps || flow.data.steps.length === 0) {
      return [];
    }

    return flow.data.steps.map(
      (step: {
        id: string;
        stepNumber: number;
        positionX: number;
        positionY: number;
        title: string;
        description: string | null;
        stepTypeName: string | null;
        positionName: string | null;
        departmentName: string | null;
        notes: string | null;
        responsiblePositionId: string | null;
      }) => {
        // Use stored position if available, otherwise auto-layout horizontally
        const x = step.positionX !== 0 ? step.positionX : step.stepNumber * 250;
        const y = step.positionY !== 0 ? step.positionY : 100;

        return {
          id: step.id,
          type: "flowStep",
          position: { x, y },
          data: { step },
          draggable: false,
        };
      },
    );
  }, [flow.data?.steps]);

  // Convert flow connections to ReactFlow edges
  const initialEdges = useMemo<Edge[]>(() => {
    if (!flow.data?.connections || flow.data.connections.length === 0) {
      return [];
    }

    return flow.data.connections.map(
      (connection: {
        id: string;
        fromStepId: string;
        toStepId: string;
        label: string | null;
      }) => {
        const sourceNode = initialNodes.find(
          (n) => n.id === connection.fromStepId,
        );
        const targetNode = initialNodes.find(
          (n) => n.id === connection.toStepId,
        );
        const handles = getHandlePositions(sourceNode, targetNode);

        return {
          id: connection.id,
          source: connection.fromStepId,
          target: connection.toStepId,
          sourceHandle: handles.sourceHandle,
          targetHandle: handles.targetHandle,
          label: connection.label || undefined,
          type: "smoothstep",
          animated: false,
          style: {
            stroke: "#3b82f6",
            strokeWidth: 3,
          },
        };
      },
    );
  }, [flow.data?.connections, initialNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes and edges when data changes
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  // Get selected step data
  const selectedStep = useMemo(() => {
    if (!selectedStepId || !flow.data?.steps) return null;
    const step = flow.data.steps.find(
      (step: { id: string }) => step.id === selectedStepId,
    );
    return step as {
      id: string;
      stepNumber: number;
      title: string;
      description: string | null;
      stepTypeName: string | null;
      positionName: string | null;
      departmentName: string | null;
      notes: string | null;
      responsiblePositionId: string | null;
    } | null;
  }, [selectedStepId, flow.data?.steps]);

  // Handle node click
  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    setSelectedStepId(node.id);
  }, []);

  if (flow.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex h-64 items-center justify-center">
            <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
          </div>
        </Main>
      </>
    );
  }

  if (!flow.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex h-64 items-center justify-center">
            <div className="text-muted-foreground">ບໍ່ພົບ Flow</div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-bold text-2xl">{flow.data.title}</h1>
              {flow.data.description && (
                <p className="text-muted-foreground text-sm">
                  {flow.data.description}
                </p>
              )}
            </div>
            {canUpdate && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => nav({ to: "/app/flows" })}
                >
                  <ArrowLeft className="h-4 w-4" />
                  ກັບຄືນ
                </Button>
                <Button
                  onClick={() =>
                    nav({ to: "/app/flows/$id/edit", params: { id } })
                  }
                >
                  <Settings className="h-4 w-4" />
                  ແກ້ໄຂຂັ້ນຕອນ
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Flow Details Card - Hidden when step is selected */}
            {!selectedStepId && (
              <div className="space-y-4 lg:col-span-1">
                <div className="space-y-4 rounded-xl border bg-card p-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-lg">ລາຍລະອຽດ Flow</h2>
                    {canUpdate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={editFlowModal.open}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        ແກ້ໄຂ
                      </Button>
                    )}
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">
                        ຊື່ Flow
                      </div>
                      <p className="mt-1">{flow.data.title}</p>
                    </div>
                    {flow.data.departmentName && (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ພະແນກ
                        </div>
                        <p className="mt-1">{flow.data.departmentName}</p>
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">
                        ສະຖານະ
                      </div>
                      <p className="mt-1">
                        {statusLabels[flow.data.status] || flow.data.status}
                      </p>
                    </div>
                    {flow.data.purpose && (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ຈຸດປະສົງ
                        </div>
                        <p className="mt-1">{flow.data.purpose}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mappings Card */}
                <div className="space-y-4 rounded-xl border bg-card p-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-lg">ການຜູກກັບ</h2>
                    {canUpdate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={createMappingModal.open}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        ເພີ່ມການຜູກກັບ
                      </Button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {mappings.data?.data && mappings.data.data.length > 0 ? (
                      mappings.data.data.map(
                        (mapping: {
                          id: string;
                          sopId: string | null;
                          sopTitle: string | null;
                          departmentId: string | null;
                          departmentName: string | null;
                          positionId: string | null;
                          positionName: string | null;
                        }) => (
                          <div
                            key={mapping.id}
                            className="flex items-start justify-between space-y-2 rounded-lg border p-3"
                          >
                            <div className="flex-1 space-y-2">
                              {mapping.sopTitle && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-muted-foreground text-sm">
                                    SOP:
                                  </span>
                                  <span className="text-sm">
                                    {mapping.sopTitle}
                                  </span>
                                  {mapping.sopId && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-5"
                                      onClick={() => {
                                        if (mapping.sopId) {
                                          nav({
                                            to: "/app/sop/$id",
                                            params: { id: mapping.sopId },
                                          });
                                        }
                                      }}
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              )}
                              {mapping.departmentName && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-muted-foreground text-sm">
                                    ພະແນກ:
                                  </span>
                                  <span className="text-sm">
                                    {mapping.departmentName}
                                  </span>
                                </div>
                              )}
                              {mapping.positionName && (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-muted-foreground text-sm">
                                    ຕໍາແໜ່ງ:
                                  </span>
                                  <span className="text-sm">
                                    {mapping.positionName}
                                  </span>
                                  {mapping.positionId && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-5"
                                      onClick={() =>
                                        window.open(
                                          `/app/positions?positionId=${mapping.positionId}`,
                                          "_blank",
                                        )
                                      }
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                            {canUpdate && (
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    setEditingMapping({
                                      id: mapping.id,
                                      sopId: mapping.sopId,
                                      positionId: mapping.positionId,
                                      departmentId: mapping.departmentId,
                                    });
                                    editMappingModal.open();
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={async () => {
                                    if (
                                      await confirm({
                                        title: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບການຜູກກັບນີ້?",
                                        description:
                                          "ການຜູກກັບນີ້ຈະຖືກລຶບອອກຈາກ Flow",
                                        actionText: "ລຶບ",
                                        cancelText: "ຍົກເລີກ",
                                        ActionProps: {
                                          variant: "destructive",
                                        },
                                      })
                                    ) {
                                      await deleteMapping.mutateAsync(
                                        mapping.id,
                                      );
                                      mappings.refetch();
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ),
                      )
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        ຍັງບໍ່ມີການຜູກກັບ
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Flow Diagram Card */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border bg-card">
                <div className="border-b p-4">
                  <h2 className="font-semibold text-lg">ຜະແນກຜັງ Flow</h2>
                  {flow.data.steps && flow.data.steps.length === 0 && (
                    <p className="mt-1 text-muted-foreground text-sm">
                      ຍັງບໍ່ມີຂັ້ນຕອນໃນ Flow ນີ້
                    </p>
                  )}
                </div>

                {flow.data.steps && flow.data.steps.length > 0 ? (
                  <div className="h-[600px] w-full">
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onNodeClick={onNodeClick}
                      nodeTypes={nodeTypes}
                      fitView
                      className="bg-background"
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable={true}
                      connectionLineStyle={{
                        stroke: "#3b82f6",
                        strokeWidth: 3,
                      }}
                      defaultEdgeOptions={{
                        type: "smoothstep",
                        style: { stroke: "#3b82f6", strokeWidth: 3 },
                      }}
                    >
                      <Controls className="border bg-background" />
                      <Background color="hsl(var(--muted))" gap={20} />
                      <MiniMap
                        className="border bg-background"
                        nodeColor={(node) => {
                          const stepType = node.data?.step?.stepTypeName;
                          if (
                            stepType?.includes("ວຽກ") ||
                            stepType?.includes("work")
                          ) {
                            return "hsl(217, 91%, 60%)";
                          }
                          if (
                            stepType?.includes("ກວດສອບ") ||
                            stepType?.includes("check")
                          ) {
                            return "hsl(43, 96%, 56%)";
                          }
                          if (
                            stepType?.includes("ອະນຸມັດ") ||
                            stepType?.includes("approve")
                          ) {
                            return "hsl(142, 76%, 36%)";
                          }
                          return "hsl(var(--muted-foreground))";
                        }}
                      />
                    </ReactFlow>
                  </div>
                ) : (
                  <div className="flex h-[400px] items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <p className="mb-2">ບໍ່ມີຂັ້ນຕອນ</p>
                      <p className="text-sm">ຍັງບໍ່ມີຂັ້ນຕອນໃນ Flow ນີ້</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step Details Side Panel */}
            {selectedStepId && selectedStep && (
              <div className="lg:col-span-1">
                <div className="rounded-xl border bg-card">
                  <div className="flex items-center justify-between border-b p-4">
                    <h2 className="font-semibold text-lg">ລາຍລະອຽດຂັ້ນຕອນ</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSelectedStepId(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-4 p-4">
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">
                        ຊື່ຂັ້ນຕອນ
                      </div>
                      <p className="mt-1 font-semibold">{selectedStep.title}</p>
                    </div>
                    {selectedStep.stepNumber !== undefined && (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ລຳດັບ
                        </div>
                        <p className="mt-1">#{selectedStep.stepNumber}</p>
                      </div>
                    )}
                    {selectedStep.stepTypeName && (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ປະເພດ
                        </div>
                        <p className="mt-1">{selectedStep.stepTypeName}</p>
                      </div>
                    )}
                    {selectedStep.description && (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ຄຳອະທິບາຍ
                        </div>
                        <p className="mt-1 text-sm">
                          {selectedStep.description}
                        </p>
                      </div>
                    )}
                    {selectedStep.positionName && (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ຜູ້ຮັບຜິດຊອບ
                        </div>
                        <p className="mt-1">{selectedStep.positionName}</p>
                      </div>
                    )}
                    {selectedStep.departmentName && (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ພະແນກ
                        </div>
                        <p className="mt-1">{selectedStep.departmentName}</p>
                      </div>
                    )}
                    {selectedStep.notes && (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ໝາຍເຫດ
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm">
                          {selectedStep.notes}
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-2 border-t pt-4">
                      {canUpdate && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() =>
                            nav({ to: "/app/flows/$id/edit", params: { id } })
                          }
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          ແກ້ໄຂ Flow
                        </Button>
                      )}
                      {mappings.data?.data &&
                        mappings.data.data.length > 0 &&
                        mappings.data.data[0]?.sopId && (
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                              const sopId = mappings.data?.data?.[0]?.sopId;
                              if (sopId) {
                                nav({
                                  to: "/app/sop/$id",
                                  params: { id: sopId },
                                });
                              }
                            }}
                          >
                            <FileText className="mr-2 h-4 w-4" />
                            ເປີດ SOP ທີ່ກ່ຽວຂ້ອງ
                          </Button>
                        )}
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          nav({ to: "/app/checklists" });
                        }}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        ເບິ່ງ Checklist
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Edit Flow Modal */}
          {canUpdate && (
            <Modal
              open={editFlowModal.isOpen}
              onOpenChange={editFlowModal.toggle}
              title="ແກ້ໄຂຂໍ້ມູນ Flow"
              size="md"
            >
              <div className="max-h-[calc(100vh-12rem)] overflow-y-auto p-4">
                <FlowForm
                  initialValues={{
                    title: flow.data.title,
                    description: flow.data.description ?? undefined,
                    purpose: flow.data.purpose ?? undefined,
                    departmentId: flow.data.departmentId ?? undefined,
                    status: flow.data.status,
                  }}
                  onSubmit={async (vals) => {
                    // Check if department is being changed
                    const isDepartmentChanging =
                      vals.departmentId !==
                      (flow.data?.departmentId ?? undefined);

                    if (isDepartmentChanging) {
                      const confirmed = await confirm({
                        title: "ປ່ຽນພະແນກຂອງ Flow",
                        description:
                          "ການປ່ຽນພະແນກຈະມີຜົນກະທົບດັ່ງນີ້:\n\n• ພະແນກຂອງທຸກຂັ້ນຕອນໃນ Flow ຈະຖືກອັບເດດເປັນພະແນກໃໝ່\n• ຕໍາແໜ່ງທີ່ບໍ່ຢູ່ໃນພະແນກໃໝ່ຈະຖືກລຶບອອກຈາກຂັ້ນຕອນ\n\nທ່ານແນ່ໃຈບໍ່ວ່າຈະດຳເນີນການຕໍ່?",
                        actionText: "ປ່ຽນພະແນກ",
                        cancelText: "ຍົກເລີກ",
                      });
                      if (!confirmed) return;
                    }

                    await updateFlow.mutateAsync({
                      title: vals.title,
                      description: vals.description ?? undefined,
                      purpose: vals.purpose ?? undefined,
                      departmentId: vals.departmentId ?? undefined,
                      status: vals.status,
                    });
                    editFlowModal.close();
                  }}
                  submitting={updateFlow.isPending}
                />
              </div>
            </Modal>
          )}

          {/* Create Mapping Modal */}
          <Modal
            open={createMappingModal.isOpen}
            onOpenChange={createMappingModal.toggle}
            title="ເພີ່ມການຜູກກັບ"
            size="md"
          >
            <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
              <FlowMappingForm
                flowDepartmentId={flow.data?.departmentId ?? null}
                onSubmit={async (values) => {
                  await createMapping.mutateAsync(values);
                  createMappingModal.close();
                  mappings.refetch();
                }}
                submitting={createMapping.isPending}
              />
            </div>
          </Modal>

          {/* Edit Mapping Modal */}
          <Modal
            open={editMappingModal.isOpen}
            onOpenChange={(open) => {
              if (!open) {
                editMappingModal.close();
                setEditingMapping(null);
              }
            }}
            title="ແກ້ໄຂການຜູກກັບ"
            size="md"
          >
            <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
              <FlowMappingForm
                flowDepartmentId={flow.data?.departmentId ?? null}
                initialValues={
                  editingMapping
                    ? {
                        sopId: editingMapping.sopId,
                        positionId: editingMapping.positionId,
                        departmentId: editingMapping.departmentId,
                      }
                    : undefined
                }
                onSubmit={async (values) => {
                  if (!editingMapping) return;
                  await updateMapping.mutateAsync({
                    mappingId: editingMapping.id,
                    data: values,
                  });
                  editMappingModal.close();
                  setEditingMapping(null);
                  mappings.refetch();
                }}
                submitting={updateMapping.isPending}
              />
            </div>
          </Modal>
        </div>
      </Main>
    </>
  );
}
